import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 120000, // 120 seconds timeout for AI content generation
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    // Get token from localStorage
    const token = localStorage.getItem('session_token');
    
    // If token exists, add it to headers
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors globally
apiClient.interceptors.response.use(
  (response) => {
    // Any status code within the range of 2xx will trigger this function
    return response;
  },
  (error) => {
    // Any status codes outside the range of 2xx will trigger this function
    
    if (error.response) {
      const { status, data } = error.response;
      
      switch (status) {
        case 401:
          // Unauthorized - token expired or invalid
          console.error('❌ Unauthorized access - redirecting to login');
          
          // Clear auth data
          localStorage.removeItem('session_token');
          localStorage.removeItem('user');
          
          // Show error message
          toast.error('Tu sesión ha expirado. Por favor inicia sesión nuevamente.');
          
          // Redirect to auth page
          setTimeout(() => {
            window.location.href = '/app/auth';
          }, 1500);
          break;
          
        case 403:
          // Forbidden
          toast.error('No tienes permisos para realizar esta acción');
          break;
          
        case 404:
          // Not found
          console.error('Resource not found:', error.config?.url);
          break;
          
        case 422:
          // Validation error
          const errorMessage = data?.detail || 'Error de validación en los datos enviados';
          toast.error(errorMessage);
          break;
          
        case 500:
          // Server error
          toast.error('Error interno del servidor. Por favor intenta nuevamente.');
          break;
          
        default:
          // Generic error
          const message = data?.detail || data?.message || 'Ha ocurrido un error inesperado';
          toast.error(message);
      }
    } else if (error.request) {
      // Request was made but no response received
      console.error('Network error:', error.message);
      toast.error('Error de conexión. Verifica tu conexión a internet.');
    } else {
      // Something else happened
      console.error('Error:', error.message);
      toast.error('Error al procesar la solicitud');
    }
    
    return Promise.reject(error);
  }
);

// Helper function to handle file uploads with progress
export const uploadFile = async (endpoint, formData, onProgress) => {
  try {
    const token = localStorage.getItem('session_token');
    
    const response = await axios.post(`${API_BASE_URL}${endpoint}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      withCredentials: true,
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentCompleted);
        }
      },
    });
    
    return response;
  } catch (error) {
    // Error handling is done by the interceptor
    throw error;
  }
};

// Helper function to check authentication status
export const checkAuth = async () => {
  try {
    const response = await apiClient.get('/auth/me');
    return response.data;
  } catch (error) {
    return null;
  }
};

// Helper function to login
export const login = async (email, password) => {
  try {
    const response = await apiClient.post('/auth/login', { email, password });
    
    if (response.data.session_token) {
      localStorage.setItem('session_token', response.data.session_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Helper function to register
export const register = async (name, email, password) => {
  try {
    const response = await apiClient.post('/auth/register', { name, email, password });
    
    if (response.data.session_token) {
      localStorage.setItem('session_token', response.data.session_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Helper function to logout
export const logout = async () => {
  try {
    await apiClient.post('/auth/logout');
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    // Clear local storage regardless of API response
    localStorage.removeItem('session_token');
    localStorage.removeItem('user');
  }
};

export default apiClient;
